import { sx } from '../../lib/sx'
import { Hov } from '../../lib/Hov'
import type { Vals } from '../../demo/vals'

/**
 * Zero-knowledge only: invite another device in the group to share alerts.
 *
 * The mapped model needs no screen like this — it already knows the fleet and
 * asks a device straight from the member who owns it. Here there is no such
 * line to follow, so the invite has to travel on its own: a code to scan, a
 * link to send, or an email. Deliberately no device list — picking a handset
 * off a roster is the mapping this model refuses to hold.
 *
 * Identical on both handsets. Neither is the authority in a pairing.
 */
export function ShareInvite({
  v,
  onBack,
  onSend,
}: {
  v: Vals
  onBack: () => void
  onSend: () => void
}) {
  return (
    <div style={sx('position:absolute;inset:0;padding-top:64px;display:flex;flex-direction:column')}>
      <div style={sx('flex:1;min-height:0;overflow:auto;padding:0 20px 24px;display:flex;flex-direction:column;gap:14px')}>
        <button
          onClick={onBack}
          style={sx(
            'width:36px;height:36px;flex:none;border-radius:var(--radius-full);border:none;' +
              'background:var(--bg-tertiary);cursor:pointer;display:flex;align-items:center;justify-content:center'
          )}
        >
          <span
            style={sx(
              'width:18px;height:18px;background:var(--text-primary);' +
                '-webkit-mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat'
            )}
          />
        </button>

        <div style={sx('display:flex;flex-direction:column;gap:8px;margin-bottom:2px')}>
          <span
            style={sx(
              'font-size:var(--fs-5xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm);line-height:34px'
            )}
          >
            Invite a device to share alerts
          </span>
          <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
            Whoever holds the device decides, on the device. You would see what it blocks — never
            which member the handset belongs to.
          </span>
        </div>

        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;' +
              'align-items:center;gap:14px'
          )}
        >
          <span
            style={sx(
              'width:172px;height:172px;border-radius:var(--radius-md);background:#fff;padding:10px;display:flex'
            )}
          >
            {v.qrAlertsImg}
          </span>
          <span style={sx('display:flex;flex-direction:column;gap:4px;align-items:center;text-align:center')}>
            <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
              Scan the code and follow instructions
            </span>
            <span
              style={sx(
                'font-size:var(--fs-2xs);font-family:ui-monospace,Menlo,monospace;' +
                  'color:var(--text-secondary);letter-spacing:.02em'
              )}
            >
              nordvpn.com/alerts/DK47-9QB2
            </span>
          </span>
        </div>

        <Hov
          as="button"
          onClick={v.openAlertShareSheet}
          style={sx(
            'width:100%;text-align:left;padding:16px 18px;border-radius:var(--radius-lg);' +
              'background:var(--bg-secondary);border:1px solid var(--border-secondary);' +
              'color:var(--text-primary);font-family:inherit;cursor:pointer;' +
              'display:flex;align-items:center;gap:12px'
          )}
          hoverStyle={sx('background:var(--bg-tertiary)')}
        >
          <span style={sx('flex:1;display:flex;flex-direction:column;gap:4px;min-width:0')}>
            <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
              Send a link
            </span>
            <span style={sx('font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)')}>
              Share the request however you already talk to them. It opens on their phone.
            </span>
          </span>
          <span
            style={sx(
              'width:16px;height:16px;flex:none;opacity:.5;background:var(--text-primary);' +
                '-webkit-mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat'
            )}
          />
        </Hov>

        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:12px'
          )}
        >
          <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
            Send the request by email
          </span>

          <span
            style={sx(
              'height:52px;border-radius:var(--radius-md);border:1px solid var(--border-input);' +
                'background:var(--bg-primary);display:flex;align-items:center;gap:10px;padding:0 14px'
            )}
          >
            <span
              style={sx(
                `width:32px;height:32px;flex:none;border-radius:var(--radius-full);background:${v.pairInviteTint};` +
                  `color:#fff;display:flex;align-items:center;justify-content:center;` +
                  `font-size:var(--fs-xs);font-weight:var(--fw-medium)`
              )}
            >
              {v.pairInviteInitials}
            </span>
            <span style={sx('flex:1;min-width:0;display:flex;flex-direction:column;gap:1px')}>
              <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
                {v.pairInviteName}
              </span>
              <span style={sx('font-size:var(--fs-2xs);color:var(--text-secondary)')}>
                {v.pairInviteSub}
              </span>
            </span>
          </span>

          <Hov
            as="button"
            onClick={onSend}
            style={sx(
              'height:48px;border-radius:var(--radius-full);border:none;background:var(--bg-accent);' +
                'color:#fff;font-family:inherit;font-size:var(--fs-sm);font-weight:var(--fw-medium);' +
                'cursor:pointer;transition:all 150ms var(--ease-out)'
            )}
            hoverStyle={sx('background:var(--bg-accent-active)')}
          >
            Send request
          </Hov>
        </div>
      </div>
    </div>
  )
}
